import {round} from 'lodash';
import {ExtraData, LatencyOptions, PerformanceStorage} from '../types';

type LifecycleIdentifier = 'start' | 'end' | 'step';
type MarkIdentifier = `${string}:${LifecycleIdentifier}`;

const buildMarkIdentifier = (
  key: string
): Record<LifecycleIdentifier, `${string}:${LifecycleIdentifier}`> => {
  return {
    start: `${key}:start`,
    step: `${key}:step`,
    end: `${key}:end`,
  };
};

const findPerformanceMarks = (performanceMark: MarkIdentifier) => {
  return performance
    .getEntriesByType('mark')
    .filter((mark) => mark.name === performanceMark) as PerformanceMark[];
};

const findPerformanceMeasures = (performanceMark: MarkIdentifier) => {
  return performance
    .getEntriesByType('measure')
    .filter((mark) => mark.name === performanceMark) as PerformanceMeasure[];
};

const addStartPoint = (
  key: string,
  extraData: ExtraData = {},
  options?: LatencyOptions
) => {
  const {start: startMarkId} = buildMarkIdentifier(key);
  const existingStartMarks = findPerformanceMarks(startMarkId);

  if (existingStartMarks.length > 0) {
    throw new Error(
      `[Latency Measurement] There is already a start mark for key: ${key}`
    );
  }

  const markOptions: PerformanceMarkOptions = {
    detail: extraData,
  };

  // If measureFromOrigin is true, set startTime to 0 to measure from the origin
  if (options?.measureFromOrigin) {
    markOptions.startTime = 0;
  }

  performance.mark(startMarkId, markOptions);
};

const addStepPoint = (key: string, name: string) => {
  const {start: startMarkId, step: stepMarkId} = buildMarkIdentifier(key);

  const existingStartMarks = findPerformanceMarks(startMarkId);

  if (existingStartMarks.length === 0) {
    throw new Error(
      `[Latency Measurement] No existing measurements for key: ${key}`
    );
  }

  if (existingStartMarks.length > 1) {
    throw new Error(
      `[Latency Measurement] Multiple start marks found for key: ${key}`
    );
  }

  performance.measure(stepMarkId, {
    detail: {
      name,
    },
    start: startMarkId,
  });
};

const addEndPoint = (key: string, extraData: ExtraData = {}) => {
  const {end: endMarkId, start: startMarkId} = buildMarkIdentifier(key);

  const existingStartMarks = findPerformanceMarks(startMarkId);

  if (existingStartMarks.length === 0) {
    throw new Error(
      `[Latency Measurement] No existing measurements for key: ${key}`
    );
  }

  if (existingStartMarks.length > 1) {
    throw new Error(
      `[Latency Measurement] Multiple start marks found for key: ${key}`
    );
  }

  performance.measure(endMarkId, {
    detail: extraData,
    start: startMarkId,
  });
};

/**
 * Implements a performance storage using the browser Performance API. Do not use
 * on its own, use it with the core utils in `../core`. If you are looking to use
 * these tools in an environment different from the browser, or want to use a custom
 * performance API, you can build your own instead of using this.
 *
 * @see ../client.ts for more information on how to use this.
 */
export const browserPerformanceApiStorage: PerformanceStorage = {
  addPoint(key, name, pointType, extraData, options) {
    if (pointType === 'start') {
      addStartPoint(key, extraData, options);
    }

    if (pointType === 'step') {
      addStepPoint(key, name);
    }

    if (pointType === 'end') {
      addEndPoint(key, extraData);
    }
  },
  clearPoints(key) {
    const {
      start: startMarkId,
      step: stepMarkId,
      end: endMarkId,
    } = buildMarkIdentifier(key);

    // These methods may not exist in test environments (e.g., Jest's jsdom)
    performance.clearMarks?.(startMarkId);
    performance.clearMeasures?.(stepMarkId);
    performance.clearMeasures?.(endMarkId);
  },
  fetchEventData(key) {
    const {
      start: startMarkId,
      step: stepMarkId,
      end: endMarkId,
    } = buildMarkIdentifier(key);

    const existingStartMarks = findPerformanceMarks(startMarkId);

    if (existingStartMarks.length === 0) {
      throw new Error(
        `[Latency Measurement] No existing measurements for key: ${key}`
      );
    }

    if (existingStartMarks.length > 1) {
      throw new Error(
        `[Latency Measurement] Multiple start marks found for key: ${key}`
      );
    }

    const existingStepMeasures = findPerformanceMeasures(stepMarkId);

    const existingStepMeasureDurations = existingStepMeasures.reduce(
      (
        durations: Record<string, number>,
        currentMeasure: PerformanceMeasure
      ) => {
        return {
          ...durations,
          [currentMeasure.detail.name]: currentMeasure.duration,
        };
      },
      {}
    );

    const existingEndMeasures = findPerformanceMeasures(endMarkId);

    if (existingEndMeasures.length !== 1) {
      throw new Error(
        `[Latency Measurement] Multiple end measures found for key: ${key}`
      );
    }

    const totalDuration = existingEndMeasures[0]?.duration;

    if (!totalDuration) {
      throw new Error(
        `[Latency Measurement] No total duration found for key: ${key}`
      );
    }

    const dataToReport = {
      ...existingStartMarks[0]?.detail,
      ...existingEndMeasures[0]?.detail,
      value: round(totalDuration, 2),
    };

    if (Object.keys(existingStepMeasureDurations).length > 0) {
      dataToReport['steps'] = existingStepMeasureDurations;
    }

    return dataToReport;
  },
};
